import React, { useState, useEffect, useMemo } from 'react';
import base32 from 'base32';
import {
  Accordion,
  AccordionItem,
  Button,
  CheckboxField,
  CopyButton,
  Paragraph,
} from '@contentful/forma-36-react-components';

import { TranslationResultTable } from '../TranslationResultTable/TranslationResultTable';
import { TRANSLATION_KEY_SPLITTER } from '../../utils/constants';
import { createLocaleSelections, LocaleSelections } from '../../utils';

import './TranslationList.css';

export const TranslationList = ({
  sdk,
  translations,
  localizedFields,
  updateSelectedLocales,
} : {
  sdk: any,
  translations: any,
  localizedFields: any,
  updateSelectedLocales: (selectedLocales: LocaleSelections) => void,
}) => {
  const [selectedLocales, setSelectedLocales] = useState<LocaleSelections>({});

  const translationsList = useMemo(() => translations?.data?.translations ?? {}, [translations]);
  const translationKeys = Object.keys(translationsList);

  useEffect(() => {
    const initialSelectedLocales = createLocaleSelections(translationsList);
    setSelectedLocales(initialSelectedLocales);
    updateSelectedLocales(initialSelectedLocales);
  }, [translationsList, updateSelectedLocales]);

  const updateSelections = (nextSelections: LocaleSelections) => {
    setSelectedLocales(nextSelections);
    updateSelectedLocales(nextSelections);
  };

  const isLocaleAuthorable = (key: string, locale: string) => {
    const value = translationsList[key]?.languages?.[locale];
    return Boolean(value) && !['en', 'en-us'].includes(locale.toLowerCase());
  };

  const areAllLocalesSelected = (key?: string) => {
    const keys = key ? [key] : translationKeys;
    const authorableLocales = keys.reduce((locales: boolean[], translationKey) => ([
      ...locales,
      ...Object.keys(translationsList[translationKey]?.languages || {})
        .filter((locale) => isLocaleAuthorable(translationKey, locale))
        .map((locale) => selectedLocales[translationKey]?.[locale])
    ]), []);
    return authorableLocales.length > 0 && authorableLocales.every(Boolean);
  };

  const selectAllCheckboxHandler = () => {
    const nextValue = !areAllLocalesSelected();
    updateSelections(translationKeys.reduce((selections, key) => ({
      ...selections,
      [key]: Object.keys(translationsList[key]?.languages || {}).reduce((locales, locale) => ({
        ...locales,
        [locale]: isLocaleAuthorable(key, locale) ? nextValue : false,
      }), {} as Record<string, boolean>),
    }), {} as LocaleSelections));
  };

  const individualCheckboxHandler = (key: string) => {
    const nextValue = !areAllLocalesSelected(key);
    updateSelections({
      ...selectedLocales,
      [key]: Object.keys(translationsList[key]?.languages || {}).reduce((locales, locale) => ({
        ...locales,
        [locale]: isLocaleAuthorable(key, locale) ? nextValue : false,
      }), {} as Record<string, boolean>),
    });
  };

  const localeCheckboxHandler = (key: string, locale: string) => {
    if (!isLocaleAuthorable(key, locale)) {
      return;
    }
    updateSelections({
      ...selectedLocales,
      [key]: {
        ...selectedLocales[key],
        [locale]: !selectedLocales[key]?.[locale],
      },
    });
  };

  return (
    <div className="translation-list-wrapper">
      <Paragraph className='translation-stats'>
        {`Number of completed keys / Total number of keys : ${translations?.data?.status?.translated} / ${translations?.data?.status?.total}`}
      </Paragraph>
      <CheckboxField
        className="select-all"
        name="select-all"
        id="select-all"
        labelText="Select all"
        onChange={selectAllCheckboxHandler}
        checked={areAllLocalesSelected()}
      />
      {
        Object.keys(translationsList).map((translationKey: string) => {
          const [entryId, , fieldName] = base32.decode(translationKey).split(TRANSLATION_KEY_SPLITTER);
          const total = translationsList?.[translationKey]?.status?.total;
          const translated = translationsList?.[translationKey]?.status?.translated;

          return (
            <Accordion key={translationKey} className="tl-accordion-wrapper">
              <AccordionItem
                title={(
                  <div className="tl-accordion-item-wrapper">
                    <div
                      className="tl-item-checkbox-div"
                      onClick={(e) => {
                        e.stopPropagation();  // Stop the event from propagating to parent AccordionItem
                      }}
                    >
                      <CheckboxField
                        className="tl-item-checkbox"
                        name={translationKey}
                        id={translationKey}
                        labelText=''
                        onChange={() => individualCheckboxHandler(translationKey)}
                        checked={areAllLocalesSelected(translationKey)}
                      />
                    </div>
                    <div className="tl-item-body">
                      <div className="tl-item-key">{ translationKey }</div>
                      <Paragraph> { `Translated: ${translated}/${total} ${translated === total ? '✅' : '❌'}` }</Paragraph>
                    </div>

                  </div>
                )}
              >
                <div className='tl-item-button-wrapper'>
                  <Button onClick={() => { sdk?.navigator?.openEntry(entryId, { slideIn: true }); }}>
                    Link to entry
                  </Button>
                  <CopyButton
                    copyValue={translationKey}
                    tooltipCopiedText="The key has been copied"
                    tooltipText="Copy this key to clipboard"
                  />
                </div>
                <Paragraph className=''>
                  { `Field Name: ${fieldName}` }
                </Paragraph>
                <Paragraph className=''>
                  { `Field Type: ${localizedFields[translationKey]}` }
                </Paragraph>
                <TranslationResultTable
                  languages={translationsList?.[translationKey]?.languages}
                  fieldType={`${localizedFields[translationKey]}`}
                  selectedLocales={selectedLocales[translationKey]}
                  onLocaleChange={(locale) => localeCheckboxHandler(translationKey, locale)}
                  selectionIdPrefix={translationKey}
                />
              </AccordionItem>
            </Accordion>
          )
        })
      }
    </div>
  );
};
